import { SelectOptionContract } from '@/shared/ui';
import z from 'zod';

export enum FacilityStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export const FacilitySchema = z.object({
  id: z.string(),
  name: z.string(),
  occupancy: z.object({
    changePct: z.number().nullable(),
    currentAvg: z.number().nullable(),
  }),
  status: z.enum(FacilityStatus),
  bedsAvg: z.number().nullable(),
  bedsHighest: z.number().nullable().optional(),
  staffAvg: z.number().nullable(),
  residents: z.object({
    currentAvg: z.number().nullable(),
    changePct: z.number().nullable(),
    residentsTotal: z.number().nullable(),
  }),
  staffingRatio: z.object({
    currentAvg: z.number().nullable(),
    changePct: z.number().nullable(),
    // HPRD for staff from Sling; the old ratio otherwise.
    metric: z.enum(['RATIO', 'HPRD']).optional(),
  }),
  payerPrivatePct: z.number().nullable().optional(),
  payerMedicaidPct: z.number().nullable().optional(),
  hasPayerTypes: z.boolean(),
  createdFlags: z.number().optional(),
  createdAlerts: z.number().optional(),
  revenue: z
    .object({
      amountUsd: z.number().nullable(),
      changePct: z.number().nullable(),
    })
    .optional(),
  monthlyRevenue: z
    .object({
      amountUsd: z.number().nullable(),
      changePct: z.number().nullable(),
    })
    .optional(),
  revenuePerResident: z
    .object({
      amountUsd: z.number().nullable(),
      changePct: z.number().nullable(),
    })
    .optional(),
  /* Financial tab columns (ST #137). Optional so the Operational table, which
     asks for none of them, keeps parsing. */
  expenses: z.number().nullable().optional(),
  expensesInsufficientData: z.boolean().optional(),
  marginAvgPct: z.number().nullable().optional(),
  revenuePerResidentAvg: z.number().nullable().optional(),
  revenuePerBedAvg: z.number().nullable().optional(),
  payerPrivateRevenuePct: z.number().nullable().optional(),
  payerMedicaidRevenuePct: z.number().nullable().optional(),
  coordinates: z
    .object({
      lat: z.number(),
      lng: z.number(),
    })
    .nullable(),
});

export const FacilitySinglePageSchema = z.object({
  id: z.string(),
  name: z.string(),
  coordinates: z
    .object({
      lat: z.number(),
      lng: z.number(),
    })
    .nullable(),
  address: z.string().nullable(),
  status: z.enum(FacilityStatus),
  phoneNumber: z.string().nullable(),
  pricingModel: z.object({ id: z.number(), name: z.string() }).nullable(),
  totalBeds: z.number().nullable(),
  totalStaff: z.number().nullable(),
  activeResidentsCount: z.number(),
  types: z
    .array(
      z.enum([
        'ASSISTED_LIVING',
        'MEMORY_CARE',
        'INDEPENDENT_LIVING',
        'SKILLED_NURSING',
      ])
    )
    .nullable(),
  yearBuilt: z.number().nullable(),
  managers: z.array(
    z.object({ id: z.number(), name: z.string(), email: z.email() })
  ),
});

export const FacilityResidentsFiltersFormSchema = z.object({
  flagged: z.boolean().optional(),
  activeAlerts: z.array(SelectOptionContract).optional(),
  payType: z.array(SelectOptionContract).optional(),
  levelOfCare: z.array(SelectOptionContract).optional(),
});
